import { FC, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { galleryService, GalleryItem as GalleryItemType } from '../services/galleryService';
import GalleryItem from '../components/GalleryItem';
import SEO from '../components/SEO';

const GalleryPage: FC = () => {
  const [galleryItems, setGalleryItems] = useState<GalleryItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Admin login state
  const [isAdmin, setIsAdmin] = useState(galleryService.isAdminLoggedIn());

  useEffect(() => {
    setIsAdmin(galleryService.isAdminLoggedIn());
  }, [refreshTrigger]);

  useEffect(() => {
    const fetchGalleryItems = async () => {
      try {
        setLoading(true);
        const items = await galleryService.getAllGalleryItems();
        // Sort by date descending (newest first)
        const sortedItems = items.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setGalleryItems(sortedItems);
        setError(null);
      } catch (err) {
        console.error("Error fetching gallery items:", err);
        setError("Nie udało się załadować galerii. Spróbuj ponownie później.");
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryItems();
  }, [refreshTrigger]);

  const handleDeleteItem = async (id: number) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten projekt z galerii?')) {
      try {
        await galleryService.deleteGalleryItem(id);
        setRefreshTrigger(prev => prev + 1);
      } catch (err) {
        console.error("Error deleting gallery item:", err);
        alert("Nie udało się usunąć projektu. Spróbuj ponownie później.");
      }
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <SEO 
        title="Galeria | Aluxim"
        description="Galeria zdjęć naszych realizacji. Zobacz projekty ogrodów zimowych, zabudowy tarasów i balkonów."
        keywords="galeria, zdjęcia, realizacje, ogrody zimowe, zabudowa tarasów, zabudowa balkonów"
      />
      <div className="container mx-auto px-4 md:px-8">
        <motion.h1 
          className="text-4xl font-bold text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Galeria
        </motion.h1>
        <motion.p 
          className="text-center text-gray-600 mb-8 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Przeglądaj nasze realizacje. Dodawanie zdjęć dostępne tylko dla administratora.
        </motion.p>



        {/* Gallery Items */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          </div>
        ) : error ? (
          <div className="text-center text-red-600 p-10 bg-white rounded-lg shadow">
            <p>{error}</p>
            <button 
              onClick={() => setRefreshTrigger(prev => prev + 1)}
              className="mt-4 bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-800"
            >
              Spróbuj ponownie
            </button>
          </div>
        ) : galleryItems.length === 0 ? (
          <div className="text-center p-10 bg-white rounded-lg shadow">
            <p className="text-gray-600">Galeria jest pusta. Bądź pierwszy i dodaj projekt!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item) => (
              <GalleryItem 
                key={item.id} 
                item={item} 
                // Only allow delete if admin
                onDelete={isAdmin ? handleDeleteItem : undefined} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;
